import type { Bula, Ref } from "@vetbolso/contracts";
import { dosesDaBula, espMatch, faixa, nomeEspecie } from "@vetbolso/core";
import { Icone, type NomeIcone } from "@vetbolso/ui";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Alertas, Refs, Rodape, refsUnicas, Secao, SpPill, type Tom } from "./comp.tsx";
import { useApp, useBase } from "./estado.tsx";
import { Casca } from "./folhas.tsx";
import "./bula.css";

type Sec = { id: string; titulo: string; curto: string; icone: NomeIcone; tom: Tom; n?: number };

const CONDUTA: Record<string, [string, string]> = {
  evitar: ["Evitar", "mal"],
  cautela: ["Cautela", "atencao"],
  monitorar: ["Monitorar", ""],
};

/** Monografia do fármaco, no formato de bulário. Cada item tem a própria fonte. */
export function FolhaBula({ id }: { id: string }) {
  const base = useBase();
  const { especie: e, abrir } = useApp();
  const corpo = useRef<HTMLDivElement>(null);
  const nav = useRef<HTMLElement>(null);
  const [ativa, setAtiva] = useState("");
  const b = base.bulario.find((x) => x.id === id);
  const doses = useMemo(() => (b ? dosesDaBula(base.doses, b.id) : []), [b, base.doses]);
  const secoes = useMemo(() => (b ? secoesDa(b, doses.length) : []), [b, doses.length]);

  // A seção visível no topo do corpo acende o atalho correspondente.
  useEffect(() => {
    const caixa = corpo.current;
    if (!caixa) return;
    const olhar = () => {
      const topo = caixa.getBoundingClientRect().top + 24;
      let atual = secoes[0]?.id ?? "";
      for (const el of caixa.querySelectorAll<HTMLElement>("[data-secao]"))
        if (el.getBoundingClientRect().top <= topo) atual = el.dataset.secao ?? atual;
      setAtiva(atual);
    };
    olhar();
    caixa.addEventListener("scroll", olhar, { passive: true });
    return () => caixa.removeEventListener("scroll", olhar);
  }, [secoes]);

  // O atalho ativo fica sempre à vista na barra rolável.
  useEffect(() => {
    const barra = nav.current;
    const botao = barra?.querySelector<HTMLElement>(`[data-alvo="${ativa}"]`);
    if (!barra || !botao) return;
    const esq = botao.offsetLeft - barra.offsetLeft;
    if (esq < barra.scrollLeft || esq + botao.offsetWidth > barra.scrollLeft + barra.clientWidth)
      barra.scrollTo({ left: esq - 16, behavior: "smooth" });
  }, [ativa]);

  if (!b) return null;
  const efeitos = [...(b.efeitos ?? [])].sort((x, y) => ordemSp(y.especie, e) - ordemSp(x.especie, e));

  const ir = (sid: string) => {
    const caixa = corpo.current;
    const alvo = caixa?.querySelector<HTMLElement>(`[data-secao="${sid}"]`);
    if (!alvo || !caixa) return;
    const top = alvo.offsetTop - caixa.offsetTop - 12;
    const inicio = caixa.scrollTop;
    const suave = !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    caixa.scrollTo({ top, behavior: suave ? "smooth" : "auto" });
    // Sem animação (aba em segundo plano, economia de energia), o salto acontece mesmo assim.
    setTimeout(() => {
      if (caixa.scrollTop === inicio && Math.abs(inicio - top) > 4) caixa.scrollTop = top;
      alvo.focus({ preventScroll: true });
    }, 450);
  };

  return (
    <Casca
      titulo={b.farmaco}
      meta={
        <>
          <span className="bula-classe">{b.classe}</span>
          {b.especies
            .filter((x) => x !== "todas")
            .map((x) => (
              <SpPill key={x} id={x} />
            ))}
        </>
      }
      corpo={{
        ref: corpo,
        antes:
          secoes.length > 1 ? (
            <nav className="bula-nav" aria-label="Seções do bulário" ref={nav}>
              {secoes.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  data-alvo={s.id}
                  className={`bula-nav-i t-${s.tom}`}
                  aria-current={ativa === s.id ? "true" : undefined}
                  onClick={() => ir(s.id)}
                >
                  <Icone nome={s.icone} className="sm" />
                  {s.curto}
                </button>
              ))}
            </nav>
          ) : null,
      }}
    >
      {b.resumo ? <p className="resumo">{b.resumo}</p> : null}
      {!espMatch(b, e) ? <div className="omit">Sem uso descrito em {nomeEspecie(e).toLowerCase()} nesta bula.</div> : null}
      <Alertas lista={b.alertas} />

      {secoes.map((s) => (
        <Secao key={s.id} titulo={s.titulo} icone={s.icone} tom={s.tom} n={s.n} data-secao={s.id} tabIndex={-1}>
          {s.id === "doses" ? (
            <>
              <div className="bula-doses">
                {doses.map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    className={`bula-dose${d.especie === e ? " atual" : ""}`}
                    onClick={() => abrir({ t: "dose", id: d.id })}
                  >
                    <span className="bula-dose-sp">
                      <SpPill id={d.especie} />
                      {d.especie === e ? <span className="tag">selecionada</span> : null}
                    </span>
                    <span className="bula-dose-v">
                      {d.semDose ? (
                        "sem dose"
                      ) : (
                        <>
                          <b>{faixa(d.min ?? 0, d.max, 3)}</b> {d.un}
                        </>
                      )}
                    </span>
                    <span className="bula-dose-q">
                      {/* Um fármaco pode ter várias doses por espécie: "Prednisolona (imunossupressora)". */}
                      {[d.farmaco.match(/\(([^)]+)\)$/)?.[1], d.via, d.intervalo].filter(Boolean).join(" · ") || d.contexto}
                    </span>
                    <Icone nome="right" className="sm bula-seta" />
                  </button>
                ))}
              </div>
              <p className="dica">
                <Icone nome="calc" className="sm" />
                Toque numa espécie para calcular pelo peso e levar à receita.
              </p>
            </>
          ) : null}

          {s.id === "apres" ? <Apresentacoes b={b} /> : null}
          {s.id === "ind" ? <Itens lista={b.indicacoes} /> : null}
          {s.id === "contra" ? <Itens lista={b.contraindicacoes} /> : null}
          {s.id === "mec" && b.mecanismo ? (
            <div className="texto">
              <p>{b.mecanismo.texto}</p>
              <Refs refs={b.mecanismo.refs} compacto />
            </div>
          ) : null}

          {s.id === "inter" ? (
            <ul className="itens">
              {b.interacoes?.map((i) => (
                <li key={`${i.agente}${i.efeito}`}>
                  <div className="linha-top">
                    <b className="linha-nome">{i.agente}</b>
                    <span className={`etq ${CONDUTA[i.conduta]?.[1] ?? ""}`}>{CONDUTA[i.conduta]?.[0]}</span>
                  </div>
                  <p>{i.efeito}</p>
                  <Refs refs={i.refs} compacto />
                </li>
              ))}
            </ul>
          ) : null}

          {s.id === "efeitos" ? (
            <ul className="itens">
              {efeitos.map((x) => (
                <li key={x.especie + x.texto}>
                  <div>{x.especie === "todas" ? <span className="tag">Todas as espécies</span> : <SpPill id={x.especie} />}</div>
                  <p>{x.texto}</p>
                  <Refs refs={x.refs} compacto />
                </li>
              ))}
            </ul>
          ) : null}
        </Secao>
      ))}

      <Rodape refs={b.refs} />
    </Casca>
  );
}

function secoesDa(b: Bula, nDoses: number): Sec[] {
  const todas: (Sec & { tem: boolean })[] = [
    { id: "doses", titulo: "Doses usuais", curto: "Doses", icone: "syringe", tom: "dose", n: nDoses, tem: nDoses > 0 },
    { id: "apres", titulo: "Apresentações", curto: "Apresentações", icone: "caixa", tom: "apres", tem: !!b.apresentacoes?.length },
    {
      id: "ind",
      titulo: "Indicações",
      curto: "Indicações",
      icone: "indicado",
      tom: "ind",
      n: b.indicacoes?.length,
      tem: !!b.indicacoes?.length,
    },
    {
      id: "contra",
      titulo: "Contraindicações e cautelas",
      curto: "Contraindicações",
      icone: "proibido",
      tom: "contra",
      n: b.contraindicacoes?.length,
      tem: !!b.contraindicacoes?.length,
    },
    { id: "mec", titulo: "Mecanismo de ação", curto: "Mecanismo", icone: "engrenagem", tom: "mec", tem: !!b.mecanismo },
    {
      id: "inter",
      titulo: "Interações medicamentosas",
      curto: "Interações",
      icone: "interacao",
      tom: "inter",
      n: b.interacoes?.length,
      tem: !!b.interacoes?.length,
    },
    {
      id: "efeitos",
      titulo: "Efeitos adversos",
      curto: "Efeitos",
      icone: "efeito",
      tom: "efeitos",
      n: b.efeitos?.length,
      tem: !!b.efeitos?.length,
    },
  ];
  return todas.filter((s) => s.tem);
}

const ordemSp = (sp: string, e: string) => (sp === e ? 2 : sp === "todas" ? 1 : 0);

/** Apresentações agrupadas por produto: o nome aparece uma vez, com cada forma embaixo. */
function Apresentacoes({ b }: { b: Bula }) {
  const grupos = new Map<string, NonNullable<Bula["apresentacoes"]>>();
  for (const a of b.apresentacoes ?? []) grupos.set(a.nome, [...(grupos.get(a.nome) ?? []), a]);
  return (
    <div className="bula-apres">
      {[...grupos].map(([nome, formas]) => {
        const p = formas[0]!;
        return (
          <div key={nome} className="bula-card">
            <div className="bula-card-top">
              <span className="bula-prod-ic" aria-hidden="true">
                <Icone nome={formas.some((f) => /xarope|solu|suspens|gotas/i.test(f.forma ?? nome)) ? "frasco" : "pill"} className="sm" />
              </span>
              <span className="bula-prod">
                <b className="linha-nome">{nome}</b>
                {p.fabricante ? <span>{p.fabricante}</span> : null}
              </span>
              {p.uso ? (
                <span className={`etq ${p.uso === "humano" ? "atencao" : "bom"}`}>{p.uso === "humano" ? "Uso humano" : "Veterinário"}</span>
              ) : null}
            </div>
            {p.forma ? (
              <dl className="bula-formas">
                {formas.map((f) => (
                  <div key={`${f.forma}${f.concentracao}`}>
                    <dt>{f.forma}</dt>
                    <dd>{f.concentracao}</dd>
                  </div>
                ))}
              </dl>
            ) : null}
            <Refs refs={refsUnicas(formas.map((f) => f.refs))} compacto />
          </div>
        );
      })}
    </div>
  );
}

function Itens({ lista }: { lista?: { texto: string; refs: Ref[] }[] }): ReactNode {
  return (
    <ul className="itens">
      {lista?.map((i) => (
        <li key={i.texto}>
          <p>{i.texto}</p>
          <Refs refs={i.refs} compacto />
        </li>
      ))}
    </ul>
  );
}
